/**
 * Full-screen Hero Section — Insolip brand (navy, grid, glow, gold rings)
 */
import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { content } from '@/landing/data/content';

const TYPING_TEXT = 'IP.';
const TYPING_INTERVAL_MS = 400;
const REWRITE_INTERVAL_MS = 20_000;

interface HeroProps {
  onPrimaryCTA: () => void;
}

export function Hero({ onPrimaryCTA }: HeroProps) {
  const [typedSuffix, setTypedSuffix] = useState('');

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let rewriteId: ReturnType<typeof setTimeout> | null = null;
    let startId: ReturnType<typeof setTimeout> | null = null;

    const runTyping = (initialDelayMs: number) => {
      startId = setTimeout(() => {
        startId = null;
        let index = 0;
        intervalId = setInterval(() => {
          index += 1;
          setTypedSuffix(TYPING_TEXT.slice(0, index));
          if (index >= TYPING_TEXT.length && intervalId) {
            clearInterval(intervalId);
            intervalId = null;
            rewriteId = setTimeout(() => {
              rewriteId = null;
              setTypedSuffix('');
              runTyping(0);
            }, REWRITE_INTERVAL_MS);
          }
        }, TYPING_INTERVAL_MS);
      }, initialDelayMs);
    };

    runTyping(1200);
    return () => {
      if (startId) clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
      if (rewriteId) clearTimeout(rewriteId);
    };
  }, []);

  const scrollToNext = () => {
    document.querySelector('#stats')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen overflow-hidden bg-[var(--navy)]">
      {/* Base + grid overlay */}
      <div className="absolute inset-0 hero-grid-overlay" />
      {/* Radial glow — top-right */}
      <div
        className="absolute top-0 right-0 w-[680px] h-[680px] -translate-y-1/2 translate-x-1/2 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(26,86,219,0.18) 0%, transparent 70%)',
        }}
      />
      {/* Concentric rings — right side */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-[500px] h-[500px] rounded-full border border-[rgba(201,168,76,0.12)] pointer-events-none" />
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-[640px] h-[640px] rounded-full border border-[rgba(201,168,76,0.07)] pointer-events-none" />

      {/* Content — centered */}
      <div className="relative z-10 min-h-screen flex items-start justify-center px-4 lg:px-8 pt-28 md:pt-32 pb-24 md:pb-28">
        <div className="container mx-auto flex flex-col items-center justify-center text-center">
          <div className="w-full max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--gold-pale)] border border-[rgba(201,168,76,0.2)] mb-8"
            >
              <span className="text-[var(--gold)] text-sm font-semibold tracking-wider uppercase">
                1,000+ Innovations Managed
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35 }}
              className="font-display text-5xl md:text-7xl lg:text-8xl font-semibold text-white mb-6 leading-none whitespace-nowrap"
            >
              <span className="hero-product-name inline-block text-center">
                {content.productName.replace(/ip$/i, '')}
                <span className="hero-gold-part text-[var(--gold)] inline-flex items-baseline relative pr-[0.2em]">
                  <span aria-hidden className="invisible select-none">{TYPING_TEXT}</span>
                  <span className="absolute left-0 bottom-0 leading-none" style={{ lineHeight: 1 }}>
                    {typedSuffix}
                    <span
                      className="hero-typing-cursor inline-block w-[0.12em] h-[0.9em] ml-0.5 bg-[var(--gold)] align-baseline"
                      aria-hidden
                    />
                  </span>
                </span>
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="text-2xl md:text-3xl font-semibold text-[var(--gold)] mb-4"
            >
              {content.tagline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.65 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7 text-left mb-10 md:mb-12 items-stretch"
            >
              {content.heroTiles.map((tile) => (
                <article key={tile.heading} className="hero-info-tile rounded-xl p-5 md:p-6 lg:p-7 h-full flex flex-col">
                  <h3 className="text-center text-[var(--gold)] text-sm md:text-base font-semibold tracking-[0.18em] mb-4">
                    {tile.heading}
                  </h3>
                  {tile.intro ? (
                    <p className="text-white/90 text-sm md:text-[15px] leading-6 mb-4">
                      {tile.intro}
                    </p>
                  ) : null}
                  <div className="space-y-2 flex-1">
                    {tile.details.map((detail, index) => {
                      const isHowItWorks = tile.heading === 'HOW IT WORKS';
                      const isWhyThisMatters = tile.heading === 'WHY THIS MATTERS';
                      const shouldUseNumbering = isHowItWorks && index < 4;
                      const shouldUseCheckmark = isWhyThisMatters && index >= 3 && index <= 8;
                      const prefix = shouldUseNumbering ? `${index + 1}. ` : '';

                      return (
                        <p key={`${tile.heading}-${index}`} className="text-white/80 text-sm md:text-[14px] leading-6">
                          {shouldUseCheckmark ? <span className="mr-1 text-[var(--gold)]">{"\u2714"}</span> : null}
                          {prefix}
                          {detail}
                        </p>
                      );
                    })}
                  </div>
                </article>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.8 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-2"
            >
              <Button
                onClick={onPrimaryCTA}
                className="insolip-cta-primary"
              >
                {content.cta.primary}
              </Button>
              <Button
                onClick={scrollToNext}
                variant="outline"
                className="insolip-cta-ghost"
              >
                {content.cta.secondary}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Gold divider below hero */}
      <div className="absolute bottom-0 left-0 right-0 gold-divider" />

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToNext}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{
          opacity: { delay: 1.5, duration: 0.6 },
          y: { duration: 1.5, repeat: Infinity, repeatType: 'loop' },
        }}
        className="hidden md:block absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-[var(--gold)] transition-colors z-20"
        aria-label="Scroll to next section"
      >
        <ChevronDown className="w-8 h-8" />
      </motion.button>
    </section>
  );
}
